import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useAuth } from '../redux/hooks/useAuth';
import AuthLoadingScreen from './AuthLoadingScreen';

const ProtectedRoute = ({ 
  children, 
  requiredPermissions = [], 
  requiredRoles = [],
  fallbackPath = '/auth/login',
  showLoading = true 
}) => {
  const { 
    isAuthenticated, 
    loading, 
    initialized,
    hasPermission, 
    hasRole, 
    hasAnyRole, 
    userPermissions, 
    userRole,
    sessionStatus,
    isSessionExpired,
    validateUserToken
  } = useAuth();
  
  const [isChecking, setIsChecking] = useState(true);
  const [sessionValid, setSessionValid] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const checkAccess = async () => {
      // Wait for authentication to be initialized
      if (!initialized) {
        return;
      }

      // If not authenticated and not loading, redirect to login
      if (!isAuthenticated && !loading) {
        setIsChecking(false);
        return;
      }

      // If still loading, wait
      if (loading) {
        return;
      }

      // If authenticated, validate session and check permissions/roles
      if (isAuthenticated) {
        // Validate session if it's expiring or expired
        if (sessionStatus?.status === 'expiring' || sessionStatus?.status === 'expired') {
          try {
            await validateUserToken();
            setSessionValid(true);
          } catch (error) {
            console.error('🔒 Session validation error:', error);
            setSessionValid(false);
            setIsChecking(false);
            return;
          }
        }

        // Check permissions if required
        if (requiredPermissions.length > 0) {
          console.log('🔒 Checking permissions:', {
            required: requiredPermissions,
            userPermissions: userPermissions,
            userRole: userRole,
            sessionStatus: sessionStatus?.status
          });
          
          const hasRequiredPermissions = requiredPermissions.every(permission => {
            const hasPerm = hasPermission(permission);
            console.log(`🔍 Permission check: ${permission} = ${hasPerm}`);
            return hasPerm;
          });
          
          console.log('🔒 Has required permissions:', hasRequiredPermissions);
          
          if (!hasRequiredPermissions) {
            setIsChecking(false);
            return;
          }
        }

        // Check roles if required
        if (requiredRoles.length > 0) {
          console.log('🔒 Checking roles:', {
            required: requiredRoles,
            userRole: userRole
          });
          
          const hasRequiredRole = hasAnyRole(requiredRoles);
          console.log('🔒 Has required role:', hasRequiredRole);
          
          if (!hasRequiredRole) {
            setIsChecking(false);
            return;
          }
        }
      }

      setIsChecking(false);
    };

    checkAccess();
  }, [
    isAuthenticated, 
    loading, 
    initialized,
    requiredPermissions, 
    requiredRoles, 
    hasPermission, 
    hasAnyRole, 
    userPermissions, 
    userRole,
    sessionStatus,
    validateUserToken
  ]);

  // Show loading spinner
  if (loading || isChecking || !initialized) {
    if (!showLoading) {
      return null;
    }
    
    return <AuthLoadingScreen 
      type="loading" 
      message={!initialized ? 'Initializing application...' : 'Checking permissions...'} 
    />;
  }

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    console.log('🔒 Not authenticated, redirecting to login');
    return <Navigate to={fallbackPath} state={{ from: location }} replace />;
  }

  // If session is invalid, redirect to login
  if (!sessionValid) {
    console.log('🔒 Session invalid, redirecting to login');
    return <Navigate to={fallbackPath} state={{ from: location }} replace />;
  }

  // If permissions/roles don't match, redirect to unauthorized
  if (requiredPermissions.length > 0 || requiredRoles.length > 0) {
    const hasPermissions = requiredPermissions.length === 0 || 
      requiredPermissions.every(permission => hasPermission(permission));
    
    const hasRoles = requiredRoles.length === 0 || 
      hasAnyRole(requiredRoles);

    if (!hasPermissions || !hasRoles) {
      console.log('🔒 Access denied, redirecting to unauthorized');
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // Access granted
  console.log('🔒 Access granted to protected route');
  return children;
};

export default ProtectedRoute;
